import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Chip from "@material-ui/core/Chip";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import api from "../../services/api";
import { Stack } from "@mui/material";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Bot, Tags } from "lucide-react";
import ChatiaDialog from "../ChatiaDialog";

const useStyles = makeStyles(theme => ({
    root: { display: "flex", flexWrap: "wrap" },
    btnWrapper: { position: "relative" },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12
    },
    content: {
        gap: 12,
        display: "flex",
        flexDirection: "column",
    },
    infoCard: {
        background: theme.mode === "dark" ? "#16191f" : "#ffffff",
        color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.1,
    },
    cardIcon: {
        width: 32,
        height: 32,
        borderRadius: 10,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
        border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
        color: theme.palette.primary.main,
        "& svg": {
            color: theme.palette.primary.main,
            stroke: theme.palette.primary.main,
        },
    },
    chipContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 12,
        padding: 10,
        minHeight: 40,
        backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
        border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
        borderRadius: 10,
        alignItems: "center",
    },
    emptySelection: {
        color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
        fontSize: 13,
    },
    tagList: {
        maxHeight: 300,
        overflow: "auto",
        padding: 0,
        ...theme.scrollbarStyles,
    },
    tagItem: {
        borderRadius: 10,
        color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        marginBottom: 4,
        padding: "7px 8px",
        "&:hover": {
            background: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
        },
    },
    checkboxCell: {
        minWidth: 28,
        display: "inline-flex",
        alignItems: "center",
    },
    cyberpunkCheckbox: {
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        width: 16,
        height: 16,
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 5,
        backgroundColor: "transparent",
        display: "inline-block",
        position: "relative",
        margin: 0,
        padding: 0,
        cursor: "pointer",
        flexShrink: 0,
        "&::before": {
            content: '""',
            backgroundColor: theme.palette.primary.main,
            display: "block",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) scale(0)",
            width: 8,
            height: 8,
            borderRadius: 3,
            transition: "all 0.3s ease-in-out",
        },
        "&:checked::before": {
            transform: "translate(-50%, -50%) scale(1)",
        },
    },
    tagDot: {
        width: 14,
        height: 14,
        borderRadius: "50%",
        marginRight: 8,
        flexShrink: 0,
    },
}));

const FlowBuilderAddTagModal = ({ open, onSave, data, onUpdate, close }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMounted = useRef(true);
    const [activeModal, setActiveModal] = useState(false);
    const [tags, setTags] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [aiTimeoutSeconds, setAiTimeoutSeconds] = useState(0);

    useEffect(() => {
        if (open === "edit") {
            (async () => {
                try {
                    const { data: tagsData } = await api.get("/tags", { params: { kanban: 0 } });
                    const regularTags = (tagsData.tags || tagsData || []).filter(t => Number(t.kanban) !== 1);
                    setTags(regularTags);
                    const existingTags = data?.data?.tags || data?.tags || [];
                    setSelectedTagIds(existingTags.map(t => t.id));
                    setAiTimeoutSeconds(data?.data?.aiTimeoutSeconds || 0);
                    setActiveModal(true);
                } catch (error) {
                    console.log(error);
                }
            })();
        } else if (open === "create") {
            (async () => {
                try {
                    const { data: tagsData } = await api.get("/tags", { params: { kanban: 0 } });
                    const regularTags = (tagsData.tags || tagsData || []).filter(t => Number(t.kanban) !== 1);
                    setTags(regularTags);
                    setSelectedTagIds([]);
                    setAiTimeoutSeconds(0);
                    setActiveModal(true);
                } catch (error) {
                    console.log(error);
                }
            })();
        }
        return () => { isMounted.current = false; };
    }, [open]);

    const handleClose = () => {
        close(null);
        setActiveModal(false);
    };

    const handleToggleTag = (tagId) => {
        setSelectedTagIds(prev =>
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    };

    const handleSave = () => {
        if (selectedTagIds.length === 0) {
            return toast.error("Selecione pelo menos uma tag");
        }
        const selectedTags = selectedTagIds.map(id => {
            const t = tags.find(tag => tag.id === id);
            return { id: t.id, name: t.name, color: t.color };
        });
        if (open === "edit") {
            onUpdate({ ...data, data: { tags: selectedTags, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 } });
        } else {
            onSave({ data: { tags: selectedTags, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0 } });
        }
        handleClose();
    };

    const title = open === "create" ? "Adicionar Tags" : "Editar Tags";

    const CardTitle = ({ icon: Icon, children }) => (
        <div className={classes.cardHeader}>
            <span className={classes.cardIcon}>
                <Icon size={16} />
            </span>
            <span>{children}</span>
        </div>
    );

    return (
        <div className={classes.root}>
            <ChatiaDialog
                open={activeModal}
                onClose={handleClose}
                title={title}
                maxWidth="md"
                contentDividers={false}
                onSave={handleSave}
                saveType="button"
                saveText={open === "create" ? "Adicionar" : "Salvar"}
            >
                <Stack className={classes.content}>
                    <Stack className={classes.infoCard}>
                        <CardTitle icon={Tags}>{title}</CardTitle>
                        <div className={classes.chipContainer}>
                            {selectedTagIds.length === 0 && (
                                <span className={classes.emptySelection}>Nenhuma tag selecionada</span>
                            )}
                            {selectedTagIds.map(id => {
                                const tag = tags.find(t => t.id === id);
                                return tag ? (
                                    <Chip
                                        key={id}
                                        label={tag.name}
                                        size="small"
                                        onDelete={() => handleToggleTag(id)}
                                        style={{
                                            backgroundColor: tag.color || "#ccc",
                                            color: "#fff",
                                        }}
                                    />
                                ) : null;
                            })}
                        </div>
                        <List dense className={classes.tagList}>
                            {tags.map(tag => (
                                <ListItem
                                    key={tag.id}
                                    button
                                    onClick={() => handleToggleTag(tag.id)}
                                    dense
                                    className={classes.tagItem}
                                >
                                    <span className={classes.checkboxCell}>
                                        <input
                                            type="checkbox"
                                            className={classes.cyberpunkCheckbox}
                                            checked={selectedTagIds.includes(tag.id)}
                                            onChange={() => handleToggleTag(tag.id)}
                                            onClick={(event) => event.stopPropagation()}
                                        />
                                    </span>
                                    <div className={classes.tagDot} style={{ backgroundColor: tag.color || "#ccc" }} />
                                    <ListItemText primary={tag.name} primaryTypographyProps={{ style: { fontSize: 13 } }} />
                                </ListItem>
                            ))}
                        </List>
                    </Stack>

                    <Stack className={classes.infoCard}>
                        <CardTitle icon={Bot}>
                            <Typography style={{ fontSize: "14px", fontWeight: "bold", color: theme.mode === "dark" ? "#e9edef" : "#111b21" }}>
                                Saída IA (tempo para IA assumir)
                            </Typography>
                        </CardTitle>
                        <TextField
                            label="Tempo em segundos (0 = desativado)"
                            type="number"
                            variant="outlined"
                            margin="dense"
                            value={aiTimeoutSeconds}
                            onChange={e => setAiTimeoutSeconds(e.target.value)}
                            fullWidth
                            InputProps={{ inputProps: { min: 0 } }}
                            helperText={aiTimeoutSeconds > 0 ? `Se o cliente não responder em ${aiTimeoutSeconds}s, a IA assume o atendimento (saída roxa)` : ""}
                        />
                    </Stack>
                </Stack>
            </ChatiaDialog>
        </div>
    );
};

export default FlowBuilderAddTagModal;
